import { useMemo } from 'react'
import { AreaChart, BarChart } from '@mantine/charts'
import { Paper, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'

import { BillingForecast } from './billingForecast'
import { formatCzk, formatCzkCompact, formatShortDay, formatWeekdayDay, niceCeil } from './format'

// Validated (dataviz palette check) for CVD separation and contrast on both the light and dark
// Mantine surfaces. Forecasts are additionally dashed / lighter so identity is never colour-only.
const ACTUAL_COLOR = 'blue.6'
const FORECAST_COLOR = 'orange.8'
const REFERENCE_COLOR = 'gray.6'
const CHART_HEIGHT = 300

const ChartCard = ({ title, caption, children }: { title: string; caption?: string; children: React.ReactNode }) => (
  <Paper p="md" radius="md">
    <Text size="sm" fw={600} mb={caption ? 2 : 'md'}>
      {title}
    </Text>
    {caption && (
      <Text size="xs" c="dimmed" mb="md">
        {caption}
      </Text>
    )}
    {children}
  </Paper>
)

/** y-axis upper bound covering every plotted value and reference line, with a little headroom. */
const upperBound = (values: (number | null)[]) => niceCeil(Math.max(0, ...values.map(value => value ?? 0)) * 1.05)

export const CumulativeBillingChart = ({ forecast }: { forecast: BillingForecast }) => {
  const { t, i18n } = useTranslation('billing')
  const locale = i18n.language

  const data = useMemo(
    () =>
      forecast.days.map(day => ({
        day: day.day,
        actual: day.actualCumulative,
        forecast: day.forecastCumulative,
      })),
    [forecast.days]
  )
  const max = upperBound([...data.flatMap(item => [item.actual, item.forecast]), forecast.order])

  return (
    <ChartCard title={t('chart.cumulativeTitle')}>
      <AreaChart
        h={CHART_HEIGHT}
        data={data}
        dataKey="day"
        series={[
          { name: 'actual', label: t('chart.actual'), color: ACTUAL_COLOR },
          { name: 'forecast', label: t('chart.forecast'), color: FORECAST_COLOR, strokeDasharray: '6 4' },
        ]}
        curveType="linear"
        strokeWidth={2}
        fillOpacity={0.12}
        // A lighter fill keeps the projected region from outweighing the actuals.
        areaProps={series => (series.name === 'forecast' ? { fillOpacity: 0.05 } : {})}
        withGradient={false}
        withDots={false}
        connectNulls={false}
        withLegend
        legendProps={{ verticalAlign: 'bottom' }}
        strokeDasharray="0"
        valueFormatter={value => formatCzk(value, locale)}
        xAxisProps={{ tickFormatter: (day: string) => formatShortDay(day, locale) }}
        tooltipProps={{ labelFormatter: (day: string) => formatWeekdayDay(day, locale) }}
        yAxisProps={{ domain: [0, max], tickFormatter: (value: number) => formatCzkCompact(value, locale), width: 80 }}
        referenceLines={
          forecast.order > 0
            ? [
                // Order and target are only ~7 % apart: label order above its line and target below its own
                // (recharts' inside*Bottom* sits above a horizontal line), on the left where actuals are low.
                {
                  y: forecast.order,
                  label: t('chart.order'),
                  labelPosition: 'insideBottomLeft',
                  color: REFERENCE_COLOR,
                },
                {
                  y: forecast.target,
                  label: t('chart.target'),
                  labelPosition: 'insideTopLeft',
                  color: REFERENCE_COLOR,
                  strokeDasharray: '2 4',
                },
              ]
            : []
        }
      />
    </ChartCard>
  )
}

export const DailyIncrementsChart = ({ forecast }: { forecast: BillingForecast }) => {
  const { t, i18n } = useTranslation('billing')
  const locale = i18n.language

  const data = useMemo(
    () =>
      forecast.days.map(day => ({
        day: day.day,
        actual: day.actualIncrement,
        forecast: day.forecastIncrement,
      })),
    [forecast.days]
  )
  const values = data.flatMap(item => [item.actual, item.forecast])
  const max = upperBound([...values, forecast.weekdayAverage, forecast.weekendAverage])
  // Corrections can make a day's increment negative.
  const min = -niceCeil(-Math.min(0, ...values.map(value => value ?? 0)) * 1.05)

  return (
    <ChartCard title={t('chart.incrementsTitle')} caption={t('chart.averagesCaption')}>
      <BarChart
        h={CHART_HEIGHT}
        data={data}
        dataKey="day"
        type="stacked"
        series={[
          { name: 'actual', label: t('chart.actual'), color: ACTUAL_COLOR },
          { name: 'forecast', label: t('chart.forecast'), color: FORECAST_COLOR },
        ]}
        fillOpacity={0.9}
        maxBarWidth={18}
        withLegend
        legendProps={{ verticalAlign: 'bottom' }}
        strokeDasharray="0"
        valueFormatter={value => formatCzk(value, locale)}
        xAxisProps={{ tickFormatter: (day: string) => formatShortDay(day, locale) }}
        tooltipProps={{ labelFormatter: (day: string) => formatWeekdayDay(day, locale) }}
        yAxisProps={{
          domain: [min, max],
          tickFormatter: (value: number) => formatCzkCompact(value, locale),
          width: 80,
        }}
        // Unlabelled: labels would sit on top of the bars. The caption explains the two lines.
        referenceLines={[
          { y: forecast.weekdayAverage, color: REFERENCE_COLOR },
          { y: forecast.weekendAverage, color: REFERENCE_COLOR, strokeDasharray: '2 4' },
        ]}
      />
    </ChartCard>
  )
}
